import { useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  WEEKDAYS_ORDERED,
  WEEKDAY_LABELS,
  type Weekday,
  type WeeklyBlock,
} from '@/types/availability'
import { validateBlock } from './utils'

interface WeeklyScheduleEditorProps {
  blocks: WeeklyBlock[]
  onChange: (blocks: WeeklyBlock[]) => void
}

function newBlockId(): string {
  return `wb-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

export function WeeklyScheduleEditor({ blocks, onChange }: WeeklyScheduleEditorProps) {
  const [expanded, setExpanded] = useState<Set<Weekday>>(new Set(WEEKDAYS_ORDERED))

  function toggleDay(day: Weekday) {
    const next = new Set(expanded)
    if (next.has(day)) next.delete(day)
    else next.add(day)
    setExpanded(next)
  }

  function addBlock(day: Weekday) {
    const newBlock: WeeklyBlock = {
      id: newBlockId(),
      weekday: day,
      start_time: '09:00',
      end_time: '17:00',
    }
    onChange([...blocks, newBlock])
  }

  function updateBlock(id: string, patch: Partial<WeeklyBlock>) {
    onChange(blocks.map((b) => (b.id === id ? { ...b, ...patch } : b)))
  }

  function removeBlock(id: string) {
    onChange(blocks.filter((b) => b.id !== id))
  }

  return (
    <div className="space-y-4">
      {WEEKDAYS_ORDERED.map((day) => {
        const dayBlocks = blocks.filter((b) => b.weekday === day)
        const isOpen = expanded.has(day)
        return (
          <div key={day} className="rounded-lg border border-slate-200">
            <div className="flex items-center justify-between px-4 py-3">
              <button
                type="button"
                className="text-left"
                onClick={() => toggleDay(day)}
                aria-expanded={isOpen}
              >
                <span className="text-sm font-medium text-slate-900">{WEEKDAY_LABELS[day]}</span>
                <span className="ml-2 text-xs text-slate-500">
                  {dayBlocks.length === 0 ? 'Unavailable' : `${dayBlocks.length} block${dayBlocks.length > 1 ? 's' : ''}`}
                </span>
              </button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => addBlock(day)}
              >
                <Plus className="h-4 w-4 mr-1" /> Add block
              </Button>
            </div>
            {isOpen && dayBlocks.length > 0 && (
              <div className="border-t border-slate-100 px-4 py-3 space-y-3">
                {dayBlocks.map((b) => {
                  const errors = validateBlock(b, blocks)
                  return (
                    <div key={b.id} className="flex items-center gap-3">
                      <div className="space-y-1">
                        <Label htmlFor={`start-${b.id}`} className="sr-only">
                          Start
                        </Label>
                        <Input
                          id={`start-${b.id}`}
                          type="time"
                          value={b.start_time}
                          onChange={(e) => updateBlock(b.id, { start_time: e.target.value })}
                          className="w-32"
                        />
                      </div>
                      <span className="text-slate-400">–</span>
                      <div className="space-y-1">
                        <Label htmlFor={`end-${b.id}`} className="sr-only">
                          End
                        </Label>
                        <Input
                          id={`end-${b.id}`}
                          type="time"
                          value={b.end_time}
                          onChange={(e) => updateBlock(b.id, { end_time: e.target.value })}
                          className="w-32"
                        />
                      </div>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        onClick={() => removeBlock(b.id)}
                        aria-label="Remove block"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      {(errors.range || errors.overlap || errors.start || errors.end) && (
                        <p className="text-xs text-red-600">
                          {errors.range ?? errors.overlap ?? errors.start ?? errors.end}
                        </p>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

